import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Days, DefaultStatus, ICategoryResponse, IService, IServiceSchedule, IServiceScheduleItem, IStatusResponse, ScheduleType } from '@bciti/types';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject, Subject, combineLatest } from 'rxjs';
import { filter, map, mergeMap, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { Organisation } from 'src/app/global/global.model';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { ServiceStateService } from 'src/app/global/services/service-state/service-state.abstract.service';
import {
    GetServiceCategoriesAction,
    GetServiceCategoriesErrorAction,
    GetServiceCategoriesSuccessAction,
    GetServiceStatusesAction,
    GetServiceStatusesErrorAction,
    GetServiceStatusesSuccessAction,
} from 'src/app/global/store/global.actions';
import { availableLanguagesSelector, organisationSelector, serviceCategoriesSelector, serviceStatusesSelector, servicesSelector } from 'src/app/global/store/global.selectors';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';
import { State } from 'src/app/store';
import { IServiceAddCtaDialogData, ServiceAddCtaDialogComponent } from '../../dialogs/service-add-cta-dialog/service-add-cta-dialog.component';
import { ServiceDetailDialogComponent } from '../../dialogs/service-detail-dialog/service-detail-dialog.component';
import { ServiceScheduleDialogComponent } from '../../dialogs/service-schedule-dialog/service-schedule-dialog.component';
import { GeoidAddressModalComponent } from '../../forms/geoid-address/modal/geoid-address-modal.component';
import { ResizeProfilePictureModalComponent } from '../../forms/resize-profile-picture-modal/resize-profile-picture-modal.component';
import { UploadModalComponent } from '../../forms/upload-modal/upload-modal.component';

const DEFAULT_FROM_TIME = '09:00';
const DEFAULT_TO_TIME = '17:00';

interface ISelectCategory extends Omit<ICategoryResponse, 'name'> {
    label: string;
    value: string;
}

interface ISelectStatus {
    label: string;
    value: ILocalStatus;
}

// FIXME:
// Fix interfaces in @bciti/types
export interface ILocalServiceSchedule extends Omit<IServiceSchedule, '_id'> {
    _id?: string;
}

export interface ILocalCategory extends ICategoryResponse {
    _id: string;
}

export interface ILocalStatus extends IStatusResponse {
    _id: string;
}

export interface ILocalService extends Omit<IService, 'schedules' | 'category' | 'availableStatus' | 'nonAvailableStatus'> {
    schedules: ILocalServiceSchedule[];
    category: ILocalCategory;
    availableStatus: ILocalStatus;
    nonAvailableStatus: ILocalStatus;
    _id?: string;
}

@Component({
    selector: 'app-service-state-form',
    templateUrl: './service-state-form.component.html',
    styleUrls: ['./service-state-form.component.scss'],
})
export class ServiceStateFormComponent implements OnInit, OnDestroy {
    @Input() service: ILocalService;

    @Input() title: string;

    @Input() submitLoading = false;

    @Output() submitForm = new EventEmitter<ILocalService>();

    destroy$ = new Subject();

    categoryUpdate$ = new ReplaySubject();

    statusesUpdate$ = new ReplaySubject();

    objectKeys = Object.keys;

    categoriesLoading = false;

    statusesLoading = false;

    loading = false;

    langs: string[];

    days: string[];

    categoryList: ISelectCategory[] = [];

    statusList: ISelectStatus[] = [];

    imageFallback: string = this.contextService.getBcitiPlusLogo();

    imagePreview: string;

    originalImage: File;

    defaultSchedule: ILocalServiceSchedule;

    customSchedules: ILocalServiceSchedule[] = [];

    organisation: Organisation;

    servicesListLength: number = 0;

    form: FormGroup = this.formBuilder.group({
        name: this.formBuilder.group({}),
        resume: this.formBuilder.group({}),
        category: new FormControl('', [Validators.required]),
        availableStatus: new FormControl('', [Validators.required]),
        nonAvailableStatus: new FormControl('', [Validators.required]),
        schedules: this.formBuilder.group({}),
        displaySchedules: new FormControl(true),
        // Optional fields
        image: new FormControl(''),
        address: new FormControl(''),
        cta: this.formBuilder.group({}),
        link: new FormControl(''),
    });

    categories$ = this.store.select(serviceCategoriesSelector).pipe(takeUntil(this.destroy$));

    statuses$ = this.store.select(serviceStatusesSelector).pipe(takeUntil(this.destroy$));

    constructor(
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        private formBuilder: FormBuilder,
        private serviceStateService: ServiceStateService,
        private translate: TranslateService,
        private store: Store<State>,
        private actions$: Actions,
        private dialog: MatDialog,
        private messageService: MessageService,
        private router: Router,
    ) {}

    get isEditMode(): boolean {
        return !!this.service;
    }

    get name(): AbstractControl {
        return this.form.get('name');
    }

    get resume(): AbstractControl {
        return this.form.get('resume');
    }

    get category(): AbstractControl {
        return this.form.get('category');
    }

    get cta(): AbstractControl {
        return this.form.get('cta');
    }

    get link(): AbstractControl {
        return this.form.get('link');
    }

    get availableStatus(): AbstractControl {
        return this.form.get('availableStatus');
    }

    get nonAvailableStatus(): AbstractControl {
        return this.form.get('nonAvailableStatus');
    }

    get formNonAvailableStatusName(): string | undefined {
        return this.nonAvailableStatus.value?.value?.name?.[this.currentLang];
    }

    get address(): AbstractControl {
        return this.form.get('address');
    }

    get formattedAddress(): string {
        if (!this.address.value) {
            return '';
        }
        return `${this.address.value.unit} ${this.address.value.number} ${this.address.value.street}, ${this.address.value.city}, ${this.address.value.province}, ${this.address.value.postCode}`;
    }

    get currentLang(): string {
        return this.translate.currentLang;
    }

    get serviceNonAvailableStatusName(): string | undefined {
        return this.service?.nonAvailableStatus?.name?.[this.currentLang];
    }

    ngOnInit(): void {
        this.actions$
            .pipe(
                ofType(GetServiceCategoriesAction),
                tap(() => (this.categoriesLoading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(GetServiceCategoriesSuccessAction, GetServiceCategoriesErrorAction),
                tap(() => (this.categoriesLoading = false)),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(GetServiceStatusesAction),
                tap(() => (this.statusesLoading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(GetServiceStatusesSuccessAction, GetServiceStatusesErrorAction),
                tap(() => (this.statusesLoading = false)),
            )
            .subscribe();

        this.categories$
            .pipe(
                take(1),
                filter((categories) => categories.length === 0),
                tap(() => this.store.dispatch(GetServiceCategoriesAction())),
            )
            .subscribe();

        this.statuses$
            .pipe(
                take(1),
                filter((statuses) => statuses.length === 0),
                tap(() => this.store.dispatch(GetServiceStatusesAction())),
            )
            .subscribe();

        this.days = Object.keys(Days).map((key) => Days[key]);

        this.store
            .select(organisationSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((org) => !!org),
                tap((org) => {
                    this.organisation = org;
                }),
            )
            .subscribe();

        this.store
            .select(servicesSelector)
            .pipe(
                takeUntil(this.destroy$),
                map((services) => {
                    this.servicesListLength = services.length;
                }),
            )
            .subscribe();

        this.store.select(availableLanguagesSelector).subscribe((langs) => {
            // set this.langs array order to have currentLang as first index
            this.langs = [this.currentLang, ...[...langs].filter((l) => this.currentLang !== l)];

            if (this.langs.length === 1) {
                const lang = this.langs[0];
                (this.form.get('name') as FormGroup).addControl(lang, new FormControl('', [Validators.required, Validators.maxLength(40)]));
                (this.form.get('resume') as FormGroup).addControl(lang, new FormControl('', [Validators.required, Validators.maxLength(250)]));
                (this.form.get('cta') as FormGroup).addControl(lang, new FormControl(''));
                return;
            }

            this.langs.forEach((lang) => {
                const nameControl = new FormControl('', [Validators.maxLength(40)]);
                const resumeControl = new FormControl('', [Validators.maxLength(250)]);

                (this.form.get('name') as FormGroup).addControl(lang, nameControl);
                (this.form.get('resume') as FormGroup).addControl(lang, resumeControl);
                (this.form.get('cta') as FormGroup).addControl(lang, new FormControl(''));

                [nameControl, resumeControl].forEach((control) => {
                    let updating = false; // Variable de contrôle pour éviter les mises à jour récursives
                    control?.valueChanges.subscribe(() => {
                        if (!updating) {
                            updating = true;
                            const otherNameControls = this.langs.filter((l) => l !== lang).map((l) => this.form.get('name.' + l) as FormControl);
                            const otherResumeControls = this.langs.filter((l) => l !== lang).map((l) => this.form.get('resume.' + l) as FormControl);

                            const isNameFilled = nameControl?.value;
                            const isResumeFilled = resumeControl?.value;

                            const otherNameControlsFilled = otherNameControls.every((otherControl) => !!otherControl?.value);
                            const otherResumeControlsFilled = otherResumeControls.every((otherControl) => !!otherControl?.value);

                            if (isNameFilled) {
                                otherNameControls.forEach((otherControl) => otherControl?.clearValidators());
                                otherNameControls.forEach((otherControl) => otherControl?.setValidators([Validators.maxLength(40)]));
                            } else if (!isNameFilled && !otherNameControlsFilled) {
                                otherNameControls.forEach((otherControl) => otherControl?.clearValidators());
                                otherNameControls.forEach((otherControl) => otherControl?.setValidators([Validators.required, Validators.maxLength(50)]));
                            }

                            if (isResumeFilled) {
                                otherResumeControls.forEach((otherControl) => otherControl?.clearValidators());
                                otherResumeControls.forEach((otherControl) => otherControl?.setValidators([Validators.maxLength(250)]));
                            } else if (!isResumeFilled && !otherResumeControlsFilled) {
                                otherResumeControls.forEach((otherControl) => otherControl?.clearValidators());
                                otherResumeControls.forEach((otherControl) => otherControl?.setValidators([Validators.required, Validators.maxLength(250)]));
                            }

                            [...otherNameControls, ...otherResumeControls].forEach((otherControl) => otherControl?.updateValueAndValidity());
                            updating = false;
                        }
                    });

                    control?.updateValueAndValidity();
                });
            });
        });

        combineLatest([this.categories$, this.statuses$, this.translate.onLangChange.pipe(startWith(''))])
            .pipe(
                map(([categories, statuses]) => {
                    this.categoryList = categories.map((cat) => {
                        const selectCategory = {
                            ...cat,
                            label: cat.name[this.translate.currentLang],
                            value: cat._id,
                        };
                        return selectCategory as ISelectCategory;
                    });

                    this.statusList = statuses.map((sta) => {
                        const statusCategory = {
                            label: sta.name[this.translate.currentLang],
                            value: { ...sta },
                            color: sta.color,
                        };
                        return statusCategory;
                    });

                    if (!this.statusesLoading && !this.categoriesLoading) {
                        this.configureForm();
                        this.loading = false;
                    }
                }),
            )
            .subscribe();
    }

    configureForm(): void {
        if (!this.isEditMode) {
            // Initial values for default schedule
            this.defaultSchedule = {
                type: ScheduleType.DEFAULT,
                schedule: this.days.reduce((acc, day) => {
                    acc[day] = [
                        {
                            from: DEFAULT_FROM_TIME,
                            to: DEFAULT_TO_TIME,
                        },
                    ];
                    return acc;
                }, {}),
            };

            this.form.patchValue({
                availableStatus: this.statusList.find((sta) => sta.value.tag === DefaultStatus.OPEN),
                nonAvailableStatus: this.statusList.find((sta) => sta.value.tag === DefaultStatus.CLOSE),
            });
        } else {
            this.form.patchValue({
                name: this.service.name,
                resume: this.service.description,
                category: this.categoryList.find((cat) => cat.value === this.service.category._id),
                cta: this.service.link ? this.service.link.label : {},
                link: this.service.link ? this.service.link.url : '',
                availableStatus: this.statusList.find((sta) => sta.value._id === this.service.availableStatus._id),
                nonAvailableStatus: this.statusList.find((sta) => sta.value._id === this.service.nonAvailableStatus._id),
                address: this.service.address,
                displaySchedules: this.service.displaySchedules,
            });
            this.imagePreview = this.service.image;
            this.defaultSchedule = this.service.schedules.find((schedule) => schedule.type === ScheduleType.DEFAULT);
            this.customSchedules = this.service.schedules.filter((schedule) => schedule.type === ScheduleType.CUSTOM);
        }
    }

    handleAddImageClick(): void {
        this.dialog
            .open(UploadModalComponent, {
                panelClass: 'service-dialog',
                data: {
                    title: this.translate.instant('pages.services-state.configuration.create.form.sections.general-information.button.image'),
                    multiple: false,
                    restrictionMessage: '',
                    allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif'],
                    maxFileSize: 20000000,
                    autoComplete: true,
                },
            })
            .afterClosed()
            .pipe(
                takeUntil(this.destroy$),
                filter((files) => !!files && files.length > 0),
                map(([file]) => {
                    this.originalImage = file;
                    return this.originalImage;
                }),
                mergeMap((file) =>
                    this.dialog
                        .open(ResizeProfilePictureModalComponent, {
                            data: {
                                image: file,
                                maintainAspectRatio: false,
                            },
                        })
                        .afterClosed(),
                ),
                tap(async (croppedFile) => {
                    if (croppedFile) {
                        this.serviceStateService
                            .postDocuments(croppedFile)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe(([img]) => {
                                this.imagePreview = img;
                            });
                    }
                }),
            )
            .subscribe();
    }

    handleEditImageClick(): void {
        this.dialog
            .open(ResizeProfilePictureModalComponent, {
                data: {
                    image: this.originalImage,
                    maintainAspectRatio: false,
                },
            })
            .afterClosed()
            .pipe(
                takeUntil(this.destroy$),
                tap(async (croppedFile) => {
                    if (croppedFile) {
                        this.serviceStateService
                            .postDocuments(croppedFile)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe(([img]) => {
                                this.imagePreview = img;
                            });
                    }
                }),
            )
            .subscribe();
    }

    handleClearImageClick(): void {
        this.imagePreview = null;
        this.originalImage = null;
    }

    handleAddLocationClick(): void {
        const dialogRef = this.dialog.open(GeoidAddressModalComponent, {
            autoFocus: false,
            data: {
                title: this.translate.instant('pages.services-state.configuration.create.form.sections.general-information.button.location'),
                streetName: this.address.value?.streetName,
                city: this.address.value?.city,
                postalCode: this.address.value?.postCode,
                unit: this.address.value?.unit,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.address.patchValue(result);
            }
        });
    }

    handleClearLocation(): void {
        this.address.reset();
    }

    handleAddCtaClick(): void {
        const name = {};
        this.langs.forEach((lang) => {
            name[lang] = {
                label: this.translate.instant(`pages.services-state.dialogs.add-cta-dialog.name.${lang}.label`),
                placeholder: this.translate.instant(`pages.services-state.dialogs.add-cta-dialog.name.${lang}.placeholder`),
                error: {
                    required: this.translate.instant(`pages.services-state.dialogs.add-cta-dialog.name.errors.required`),
                },
            };
        });

        const data: IServiceAddCtaDialogData = {
            cta: this.cta.value,
            link: this.link.value,
            langs: this.langs,
            translatedValues: {
                title: this.translate.instant('pages.services-state.dialogs.add-cta-dialog.title'),
                infos: this.translate.instant('pages.services-state.dialogs.add-cta-dialog.infos'),
                name,
                link: {
                    label: this.translate.instant('pages.services-state.dialogs.add-cta-dialog.link.label'),
                    placeholder: this.translate.instant('pages.services-state.dialogs.add-cta-dialog.link.placeholder'),
                    error: {
                        required: this.translate.instant('pages.services-state.dialogs.add-cta-dialog.link.errors.required'),
                    },
                },
                cancelButton: {
                    text: this.translate.instant('common.cancel'),
                },
                submitButton: {
                    text: this.translate.instant('common.submit'),
                },
            },
        };

        const dialogRef = this.dialog.open(ServiceAddCtaDialogComponent, {
            data,
            autoFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.cta.patchValue(result.cta);
                this.link.patchValue(result.link);
            }
        });
    }

    handleClearCta(): void {
        this.cta.reset();
        this.link.reset();
    }

    startCreatingDefaultSchedule(): void {
        const data = {
            isCustom: false,
            langs: this.langs,
            translatedValues: {
                title: this.translate.instant('pages.services-state.configuration.create.form.sections.schedules.default-schedule.dialogTitle'),
                infos: this.translate.instant('pages.services-state.configuration.create.form.sections.schedules.default-schedule.infos'),
            },
            days: this.days,
            schedule: this.defaultSchedule,
            // If we are in edit mode, then `service` will be populated
            // if we are creating a new service, `service` will be undefined and we should use the selected dropdown value
            availableStatus: this.service?.availableStatus || this.availableStatus.value.value,
            nonAvailableStatus: this.service?.nonAvailableStatus || this.nonAvailableStatus.value.value,
        };

        const dialogRef = this.dialog.open(ServiceScheduleDialogComponent, {
            data,
            autoFocus: false,
            panelClass: ['bciti-dialog', 'bciti-dialog--sm'],
        });

        dialogRef.afterClosed().subscribe((schedule: IServiceSchedule) => {
            if (!schedule) {
                return;
            }
            this.defaultSchedule.schedule = schedule.schedule;
            this.messageService.success(this.translate.instant('common.success'));
        });
    }

    addCustomSchedule(): void {
        const newCustomSchedule = {
            type: ScheduleType.CUSTOM,
            schedule: null,
        };

        this.customSchedules.push(newCustomSchedule);

        this.startCreatingCustomSchedule(newCustomSchedule, this.customSchedules.length - 1);
    }

    startCreatingCustomSchedule(item, index): void {
        const data = {
            isCustom: true,
            langs: this.langs,
            translatedValues: {
                title: this.translate.instant('pages.services-state.configuration.create.form.sections.schedules.custom-schedule.button'),
                infos: this.translate.instant('pages.services-state.configuration.create.form.sections.schedules.custom-schedule.infos'),
            },
            days: this.days,
            schedule: item,
        };

        const dialogRef = this.dialog.open(ServiceScheduleDialogComponent, {
            data,
            autoFocus: false,
            panelClass: ['bciti-dialog', 'bciti-dialog--md'],
        });

        dialogRef.afterClosed().subscribe((schedule: ILocalServiceSchedule) => {
            if (!schedule) {
                return;
            }
            this.customSchedules[index] = schedule;
            this.messageService.success(this.translate.instant('common.success'));
        });
    }

    handleDeleteCustomSchedule(index): void {
        this.customSchedules.splice(index, 1);
    }

    getDayIndex(day: Days): number {
        // eslint-disable-next-line default-case
        switch (day) {
            case Days.MONDAY:
                return 1;
            case Days.TUESDAY:
                return 2;
            case Days.WEDNESDAY:
                return 3;
            case Days.THURSDAY:
                return 4;
            case Days.FRIDAY:
                return 5;
            case Days.SATURDAY:
                return 6;
            case Days.SUNDAY:
                return 7;
        }
    }

    getSortedSchedule(schedule: IServiceScheduleItem): any {
        const days = Object.keys(schedule);
        const sortedDays = days.sort((a, b) => {
            const dayA = this.getDayIndex(a as Days);
            const dayB = this.getDayIndex(b as Days);
            return dayA - dayB;
        });

        const sorted = {};
        sortedDays.forEach((day) => {
            sorted[day] = schedule[day];
        });
        return sorted;
    }

    buildPayload(): ILocalService {
        const schedules = [this.defaultSchedule];

        if (this.customSchedules.length > 0) {
            const filteredCustomSchedules = this.customSchedules.filter((schedule) => schedule.schedule && Object.keys(schedule.schedule).length > 0);
            schedules.push(...filteredCustomSchedules);
        }

        const link = this.link.value
            ? {
                  url: this.link.value,
                  label: this.cta.value,
              }
            : null;

        const address = this.address.value
            ? {
                  ...this.address.value,
                  typeStreet: '',
                  direction: '',
              }
            : null;

        const image = this.imagePreview ? this.imagePreview : '';

        let payload: ILocalService = {
            name: this.form.value.name,
            description: this.form.value.resume,
            organisation: this.organisation.tag,
            link, // optional
            image, // optional
            address, // optional
            category: this.form.value.category.value,
            availableStatus: this.form.value.availableStatus.value._id,
            nonAvailableStatus: this.form.value.nonAvailableStatus.value._id,
            order: this.service?.order || this.servicesListLength + 1,
            starred: this.service?.starred || false,
            override: this.service?.override || null,
            schedules,
            displaySchedules: this.form.get('displaySchedules').value,
        };

        if (this.isEditMode) {
            payload['_id'] = this.service._id;
        }
        return payload;
    }

    handlePreviewServiceClick(): void {
        this.dialog.open(ServiceDetailDialogComponent, {
            data: {
                service: this.buildPayload(),
                translatedValues: {
                    dialogTitle: this.translate.instant('pages.services-state.configuration.preview-dialog.title'),
                    operationHoursTitle: this.translate.instant('pages.services-state.configuration.preview-dialog.operation-hours'),
                },
                lang: this.translate.currentLang,
            },
            autoFocus: false,
            panelClass: ['service-details-dialog', 'bciti-dialog', 'bciti-dialog--md'],
        });
    }

    handleSubmit(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        this.submitForm.emit(this.buildPayload());
    }

    public goBack(): void {
        this.router.navigate(['services']);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
